/**
 * Encapsulates the three sources that drive the Sidebar alert badges:
 *
 *   1. /alerts/unread-count — 30s poll, reset on /alerts route.
 *   2. /api/notifications/ws — live WS bumps the counter the moment the
 *      backend writes a notification (reconnects with backoff).
 *   3. /admin/alerts/count — super_admin only, 60s poll for system errors.
 *
 * Returns `{ alertCount, systemAlertCount }`. Kept as a hook so the Sidebar
 * orchestrator stays declarative.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

export function useAlertCounts(user) {
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  const [systemAlertCount, setSystemAlertCount] = useState(0);

  // Unread alert count — primary path is the live WS (/api/notifications/ws),
  // which bumps the badge the instant a notification is written. The 30s poll
  // below is the fallback when the WS can't connect (proxy, offline, etc.).
  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    const fetchCount = () => {
      api
        .get("/alerts/unread-count")
        .then((r) => setAlertCount(r.data?.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // AlertsPage hace mark-all-read en background al abrirse; reseteamos el badge
  // local de forma optimista para que el punto rojo desaparezca al instante.
  useEffect(() => {
    if (location.pathname === "/alerts") setAlertCount(0);
  }, [location.pathname]);

  // Live WS — bumps the badge the moment a notification is emitted anywhere
  // in the backend. Poll above keeps working as a safety net if the WS drops.
  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    const token = localStorage.getItem("altiv_token");
    if (!token) return;
    const apiUrl = import.meta.env.VITE_BACKEND_URL || "";
    const wsBase = apiUrl.replace(/^http/, "ws");
    if (!wsBase) return;

    let ws;
    let retryTimer;
    let closed = false;

    const connect = () => {
      try {
        ws = new WebSocket(`${wsBase}/api/notifications/ws?token=${encodeURIComponent(token)}`);
      } catch {
        return;
      }
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg?.kind === "notification") {
            setAlertCount((n) => n + 1);
          }
        } catch {}
      };
      ws.onclose = () => {
        if (closed) return;
        // Reconnect with simple backoff — browsers will nudge us here on
        // network changes, laptop wake, etc.
        retryTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    };
    connect();

    return () => {
      closed = true;
      clearTimeout(retryTimer);
      try {
        ws && ws.close();
      } catch {}
    };
  }, [user]);

  // Super-admin only: poll for system alerts (errors, parser drift, etc).
  useEffect(() => {
    if (!user || user.role !== "super_admin") return;
    const fetchCount = () => {
      api
        .get("/admin/alerts/count")
        .then((r) => setSystemAlertCount(r.data?.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  return { alertCount, systemAlertCount };
}
